import * as SelectPrimitive from "@radix-ui/react-select";
import * as React from "react";

import { useIsMobile } from "../../hooks/useIsMobile";
import { FieldDescription } from "../input/field";
import { Label } from "../Label";
import { EnhancedSelectRoot } from "./enhanced-select-root";
import { SelectContext } from "./primitives/context";
import type { SelectProps } from "./types";

export const SelectRoot = ({
  searchable = false,
  creatable = false,
  multiple: multipleProp,
  placeholder,
  label,
  description,
  value,
  defaultValue,
  onOpenChange,
  open: controlledOpen,
  defaultOpen,
  disabled,
  onValueChange,
  required,
  name,
  dir,
  children,
  ...rest
}: SelectProps) => {
  const isMobile = useIsMobile();
  const multiple = creatable ? true : multipleProp;
  const effectiveSearchable = creatable ? true : searchable;
  const isMulti = multiple === true;
  const isSimple = !isMobile && !effectiveSearchable && !isMulti;

  const simpleSelectContext = React.useMemo(
    () => ({
      isSimple: true as const,
      isMobile: false as const,
      open: false as const,
      handleOpenChange: () => {},
      selectedValues: [] as string[],
      selectedValueSet: new Set<string>(),
      handleItemSelect: () => {},
      removeValue: () => {},
      search: "" as const,
      setSearch: () => {},
      searchable: false as const,
      creatable: false as const,
      multiple: false as const,
      registerItem: () => {},
      itemLabelsByValue: { current: new Map<string, string>() },
    }),
    [],
  );

  // -------------------------------------------------------------------------
  // Simple Radix branch — desktop-only item-aligned positioning.
  // Mobile single selects intentionally use the enhanced branch for drawer UX.
  // -------------------------------------------------------------------------
  if (isSimple) {
    return (
      <>
        {label ? <Label>{label}</Label> : null}
        <div className="relative w-full text-sm text-black">
          <SelectContext value={simpleSelectContext}>
            <SelectPrimitive.Root
              value={value as string | undefined}
              defaultValue={defaultValue as string | undefined}
              onValueChange={onValueChange as ((v: string) => void) | undefined}
              disabled={disabled}
              required={required}
              name={name}
              dir={dir}
              open={controlledOpen}
              defaultOpen={defaultOpen}
              onOpenChange={onOpenChange}
              {...rest}
            >
              {children}
            </SelectPrimitive.Root>
          </SelectContext>
        </div>
        {description ? (
          <FieldDescription>{description}</FieldDescription>
        ) : null}
      </>
    );
  }

  // -------------------------------------------------------------------------
  // Enhanced branch — Popover + Command (searchable || multiple)
  // -------------------------------------------------------------------------
  if (isMulti) {
    return (
      <EnhancedSelectRoot
        searchable={effectiveSearchable}
        creatable={creatable}
        multiple
        placeholder={placeholder}
        label={label}
        description={description}
        value={value as string[] | undefined}
        defaultValue={defaultValue as string[] | undefined}
        onOpenChange={onOpenChange}
        open={controlledOpen}
        defaultOpen={defaultOpen}
        disabled={disabled}
        onValueChange={onValueChange as ((v: string[]) => void) | undefined}
        required={required}
        name={name}
        dir={dir}
        {...rest}
      >
        {children}
      </EnhancedSelectRoot>
    );
  }

  return (
    <EnhancedSelectRoot
      searchable={searchable}
      placeholder={placeholder}
      label={label}
      description={description}
      value={value as string | undefined}
      defaultValue={defaultValue as string | undefined}
      onOpenChange={onOpenChange}
      open={controlledOpen}
      defaultOpen={defaultOpen}
      disabled={disabled}
      onValueChange={onValueChange as ((v: string) => void) | undefined}
      required={required}
      name={name}
      dir={dir}
      {...rest}
    >
      {children}
    </EnhancedSelectRoot>
  );
};
